import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import {
  createVehicleLookupAlias,
  createVehicleMake,
  fetchVehicleLookupAliases,
  fetchVehicleMakes,
  fetchVehicleModels,
  fetchVehicleBodyTypes,
  fetchVehicleFuelTypes,
  fetchVehicleTransmissions,
  fetchVehicleClasses,
  fetchVehicleTrims,
  updateVehicleLookupAlias,
  updateVehicleMake,
} from "../../../../services/vehicleLookupApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";

const TABS = [
  { id: "makes", label: "Makes", load: fetchVehicleMakes },
  { id: "models", label: "Models", load: fetchVehicleModels },
  { id: "trims", label: "Trims", load: fetchVehicleTrims },
  { id: "body-types", label: "Body Types", load: fetchVehicleBodyTypes },
  { id: "fuel-types", label: "Fuel Types", load: fetchVehicleFuelTypes },
  { id: "transmissions", label: "Transmissions", load: fetchVehicleTransmissions },
  { id: "classes", label: "Vehicle Classes", load: fetchVehicleClasses },
  { id: "aliases", label: "Aliases", load: fetchVehicleLookupAliases },
];

function formatRow(item, tabId) {
  if (tabId === "aliases") {
    return {
      id: item.id,
      col1: item.lookupType,
      col2: item.rawValue,
      col3: item.normalizedValue || "—",
      col4: item.isActive ? "Active" : "Inactive",
    };
  }
  if (tabId === "models") {
    return {
      id: item.id,
      col1: item.makeName || item.makeKey,
      col2: item.name,
      col3: item.key,
      col4: item.isActive ? "Active" : "Inactive",
    };
  }
  if (tabId === "trims") {
    return {
      id: item.id,
      col1: `${item.makeKey || ""} ${item.modelName || ""}`.trim(),
      col2: item.name,
      col3: item.key,
      col4: item.isActive ? "Active" : "Inactive",
    };
  }
  return {
    id: item.id,
    col1: item.name,
    col2: item.key,
    col3: item.sortOrder ?? "—",
    col4: item.isActive ? "Active" : "Inactive",
  };
}

const VehicleLookupsPage = ({ onClose }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [activeTab, setActiveTab] = useState("makes");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");

  const [makeForm, setMakeForm] = useState({ makeKey: "", makeName: "", sortOrder: 100 });
  const [aliasForm, setAliasForm] = useState({
    lookupType: "FUEL_TYPE",
    rawValue: "",
    normalizedValue: "",
    confidence: 1,
  });

  const tabConfig = useMemo(() => TABS.find((t) => t.id === activeTab), [activeTab]);

  const loadItems = useCallback(async () => {
    if (!tabConfig) return;
    try {
      setLoading(true);
      setErrorMessage("");
      const data = await tabConfig.load({
        search: search.trim() || undefined,
        isActive: activeOnly ? true : undefined,
      });
      setItems(data);
    } catch (error) {
      setItems([]);
      setErrorMessage(error.message || "Unable to load lookup data.");
    } finally {
      setLoading(false);
    }
  }, [tabConfig, search, activeOnly]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleCreateMake = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    try {
      await createVehicleMake({
        makeKey: makeForm.makeKey.trim().toUpperCase().replace(/\s+/g, "_"),
        makeName: makeForm.makeName.trim(),
        sortOrder: Number(makeForm.sortOrder) || 100,
      });
      setMakeForm({ makeKey: "", makeName: "", sortOrder: 100 });
      setToast("Make created.");
      loadItems();
    } catch (error) {
      setToast(error.message || "Create failed.");
    }
  };

  const handleCreateAlias = async (event) => {
    event.preventDefault();
    if (!canManage) return;
    try {
      await createVehicleLookupAlias({
        lookupType: aliasForm.lookupType,
        rawValue: aliasForm.rawValue.trim(),
        normalizedValue: aliasForm.normalizedValue.trim() || null,
        confidence: Number(aliasForm.confidence) || 1,
      });
      setAliasForm({ lookupType: "FUEL_TYPE", rawValue: "", normalizedValue: "", confidence: 1 });
      setToast("Alias created.");
      loadItems();
    } catch (error) {
      setToast(error.message || "Create failed.");
    }
  };

  const toggleMakeActive = async (item) => {
    if (!canManage || activeTab !== "makes") return;
    try {
      await updateVehicleMake(item.id, { isActive: item.col4 !== "Active" });
      setToast("Make updated.");
      loadItems();
    } catch (error) {
      setToast(error.message || "Update failed.");
    }
  };

  const toggleAliasActive = async (item) => {
    if (!canManage || activeTab !== "aliases") return;
    try {
      await updateVehicleLookupAlias(item.id, { isActive: item.col4 !== "Active" });
      setToast("Alias updated.");
      loadItems();
    } catch (error) {
      setToast(error.message || "Update failed.");
    }
  };

  const headers =
    activeTab === "aliases"
      ? ["Type", "Raw value", "Normalized", "Status"]
      : activeTab === "models" || activeTab === "trims"
        ? ["Make / model", "Name", "Key", "Status"]
        : ["Name", "Key", "Sort", "Status"];

  return (
    <div className="admin-dashboard admin-vehicle-lookups-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dash__header">
        <div>
          <p className="admin-dash__eyebrow">Mobility · Vehicle Intelligence</p>
          <h1 className="admin-dash__title">Vehicle Lookups</h1>
          <p className="admin-dash__subtitle">
            Reference data for makes, models, trims, and normalization aliases (Medellín / Colombia
            market).
          </p>
        </div>
        <div className="admin-dash__header-actions">
          {onClose ? (
            <button type="button" className="admin-dash__ghost-btn" onClick={onClose}>
              Back
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-toast">{toast}</p> : null}

      <nav className="admin-vehicle-lookups-tabs" aria-label="Lookup sections">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? "is-active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="admin-vehicle-lookups-toolbar">
        <input
          type="search"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search lookups"
        />
        <label>
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          Active only
        </label>
        <button type="button" className="admin-dash__ghost-btn" onClick={loadItems}>
          Refresh
        </button>
      </div>

      {canManage && activeTab === "makes" ? (
        <form className="admin-vehicle-lookups-form" onSubmit={handleCreateMake}>
          <h2>Add make</h2>
          <input
            placeholder="make_key (RENAULT)"
            value={makeForm.makeKey}
            onChange={(e) => setMakeForm((f) => ({ ...f, makeKey: e.target.value }))}
            required
          />
          <input
            placeholder="Display name"
            value={makeForm.makeName}
            onChange={(e) => setMakeForm((f) => ({ ...f, makeName: e.target.value }))}
            required
          />
          <button type="submit" className="admin-dash__primary-btn">
            Create make
          </button>
        </form>
      ) : null}

      {canManage && activeTab === "aliases" ? (
        <form className="admin-vehicle-lookups-form" onSubmit={handleCreateAlias}>
          <h2>Add alias</h2>
          <select
            value={aliasForm.lookupType}
            onChange={(e) => setAliasForm((f) => ({ ...f, lookupType: e.target.value }))}
          >
            <option value="MAKE">MAKE</option>
            <option value="MODEL">MODEL</option>
            <option value="FUEL_TYPE">FUEL_TYPE</option>
            <option value="TRANSMISSION">TRANSMISSION</option>
            <option value="BODY_TYPE">BODY_TYPE</option>
            <option value="VEHICLE_CLASS">VEHICLE_CLASS</option>
          </select>
          <input
            placeholder="Raw value"
            value={aliasForm.rawValue}
            onChange={(e) => setAliasForm((f) => ({ ...f, rawValue: e.target.value }))}
            required
          />
          <input
            placeholder="Normalized key"
            value={aliasForm.normalizedValue}
            onChange={(e) => setAliasForm((f) => ({ ...f, normalizedValue: e.target.value }))}
          />
          <button type="submit" className="admin-dash__primary-btn">
            Create alias
          </button>
        </form>
      ) : null}

      {loading ? (
        <AdminStatePanel message="Loading lookup data…" />
      ) : errorMessage ? (
        <AdminStatePanel message={errorMessage} variant="error" />
      ) : (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h}>{h}</th>
                ))}
                {canManage && (activeTab === "makes" || activeTab === "aliases") ? (
                  <th>Actions</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const row = formatRow(item, activeTab);
                return (
                  <tr key={row.id}>
                    <td>{row.col1}</td>
                    <td>{row.col2}</td>
                    <td>{row.col3}</td>
                    <td>{row.col4}</td>
                    {canManage && activeTab === "makes" ? (
                      <td>
                        <button type="button" onClick={() => toggleMakeActive(row)}>
                          {row.col4 === "Active" ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    ) : null}
                    {canManage && activeTab === "aliases" ? (
                      <td>
                        <button type="button" onClick={() => toggleAliasActive(row)}>
                          {row.col4 === "Active" ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!items.length ? <p className="admin-table-empty">No records match this filter.</p> : null}
        </AdminTableContainer>
      )}
    </div>
  );
};

export default VehicleLookupsPage;
